import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessAlert, showError } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import CollapseHeader from '../../../../core/common/collapse-header';
import { all_routes } from '../../../router/all_routes';

interface Vehicle {
  id: string;
  numberPlate: string;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface FormData {
  numberPlate: string;
  supervisorEmpNo: string;
  serviceType: string;
  serviceDate: string;
  serviceKms: string;
}

const serviceTypes = ['GENERAL', 'ADVANCED', 'TYRE_CHANGE', 'OTHER'];
const pad2 = (n: number) => String(n).padStart(2, '0');
const getNowLocalMinuteStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const navItems = [
  { label: 'Service Dashboard', icon: 'ti ti-car', link: all_routes.service, permission: 'SERVICE_MANAGER' },
  { label: 'Add Service', icon: 'ti ti-plus', link: all_routes.addservice, permission: 'SERVICE_MANAGER' },
];

const AddServicePage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
  numberPlate: '',
  supervisorEmpNo: '',
  serviceType: '',
  serviceDate: getNowLocalMinuteStr(),   // ← start at now
  serviceKms: '',
});

const [minServiceDate, setMinServiceDate] = useState<string>(getNowLocalMinuteStr());
const refreshMinNow = () => setMinServiceDate(getNowLocalMinuteStr());


  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const locationHook = useLocation();

  // Universal handler
  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > = (e) => {
    const { name, value } = e.currentTarget;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Check permission on component mount
  useEffect(() => {
    if (!checkPermission('SERVICE_MANAGER')) {
      showError('You do not have permission to add services.');
      navigate(all_routes.service);
    }
  }, [navigate]);

  // Fetch vehicles and drivers
  useEffect(() => {
    const fetchData = async () => {
      if (!checkPermission('SERVICE_MANAGER')) return;

      setLoading(true);
      setError(null);
      try {
        // Fetch vehicles
        const vehicleResponse = await apiDefinitions.getVehicles(1);
        const vehicleData = vehicleResponse.data?.data || [];
        if (Array.isArray(vehicleData) && vehicleData.length > 0) {
          setVehicles(
            vehicleData.map((v: any) => ({
              id: v.vehicleId,
              numberPlate: v.numberPlate,
            }))
          );
        } else {
          setError('No vehicles found. Please add vehicles first.');
          showError('No vehicles found. Please add vehicles first.');
        }

        // Fetch drivers
        const driverResponse = await apiDefinitions.getUsersByType('driver', {
          filterType: '',
          filterValue: '',
          date: '',
        });
        const driverData = driverResponse.data?.data?.users || [];
        if (Array.isArray(driverData) && driverData.length > 0) {
          setDrivers(
            driverData.map((d: any) => ({
              empNumber: d.empNumber.toString(),
              name: d.name || 'Unknown',
            }))
          );
        } else {
          setError('No drivers found. Please add drivers first.');
          showError('No drivers found. Please add drivers first.');
        }
      } catch (err: any) {
        const errorMessage = `Failed to fetch data: ${err.message || 'Unknown error'}`;
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numberPlate || !formData.supervisorEmpNo || !formData.serviceType || !formData.serviceKms) {
      setError('All fields are required.');
      showError('All fields are required.');
      return;
    }

    const payload = {
      numberPlate: formData.numberPlate,
      supervisorEmpNo: parseInt(formData.supervisorEmpNo),
      serviceType: formData.serviceType,
      serviceDate: new Date(formData.serviceDate).toISOString(),
      serviceKms: parseInt(formData.serviceKms) || 0,
    };

    try {
      setLoading(true);
      setError(null);
      const response = await apiDefinitions.addVehicleServiceData(payload);
      if (response.data.status === 'S1000') {
        showSuccessAlert("Service added successfully");
        navigate(all_routes.service);
      } else {
        throw new Error(response.data.statusDescription);
      }
    } catch (err: any) {
      const errorMessage = `Failed to save service: ${err.message || 'Unknown error'}`;
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleCancel = () => {
    navigate(all_routes.service);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title fw-bold text-dark">Vehicle Management/Service/Add Service</h4>
            </div>
          <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
                      <div className="card-body pb-0 pt-2">
                        <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: "12px", display: "flex" }}>
                          {navItems.map((item, index) =>
                            item.permission && checkPermission(item.permission) ? (
                              <li className="nav-item me-item" key={index}>
                                <Link
                                  to={item.link}
                                  className={`nav-link px-0 ${index === 1? "active" : ""}`}
                                >
                                  <i className={item.icon}></i> {item.label}
                                </Link>
                              </li>
                            ) : null
                          )}
                        </ul>
                      </div>
                    </div>


        <div className="card ">
          <div className="card-body pb-0 pt-2">


            <Container>
              <Row className="justify-content-md-center">
                <Col md={8}>
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="d-flex align-items-center">
                      <i className="ti ti-alert-circle me-2 fs-5" />
                      {error}
                    </Alert>
                  )}
                  {loading && (
                    <Alert variant="info" className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Loading...
                    </Alert>
                  )}

                  <div className="card shadow-sm border-0 p-4">
                    <Form onSubmit={handleSubmit}>
                      <Row className="g-4">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted">Number Plate</Form.Label>
                            <Form.Select
                              name="numberPlate"
                              value={formData.numberPlate}
                              onChange={handleChange}
                              required
                              disabled={loading || vehicles.length === 0}
                              className="form-select-lg"
                            >
                              <option value="">
                                {vehicles.length === 0 ? 'No vehicles available' : 'Select Number Plate'}
                              </option>
                              {vehicles.map((vehicle) => (
                                <option key={vehicle.id} value={vehicle.numberPlate}>
                                  {vehicle.numberPlate}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted">Supervisor</Form.Label>
                            <Form.Select
                              name="supervisorEmpNo"
                              value={formData.supervisorEmpNo}
                              onChange={handleChange}
                              required
                              disabled={loading || drivers.length === 0}
                              className="form-select-lg"
                            >
                              <option value="">
                                {drivers.length === 0 ? 'No supervisors available' : 'Select Supervisor'}
                              </option>
                              {drivers.map((driver) => (
                                <option key={driver.empNumber} value={driver.empNumber}>
                                  {driver.name} ({driver.empNumber})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted">Service Type</Form.Label>
                            <Form.Select
                              name="serviceType"
                              value={formData.serviceType}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              className="form-select-lg"
                            >
                              <option value="">Select Service Type</option>
                              {serviceTypes.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted">Service Date</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                name="serviceDate"
                                value={formData.serviceDate}
                                onChange={handleChange}
                                onFocus={refreshMinNow}   // keep “min” fresh
                                min={minServiceDate}      // correct format: YYYY-MM-DDTHH:mm
                                required
                                disabled={loading}
                                className="form-control-lg"
                              />

                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted">Service Kilometers</Form.Label>
                            <Form.Control
                              type="number"
                              name="serviceKms"
                              value={formData.serviceKms}
                              onChange={handleChange}
                              min="0"
                              required
                              disabled={loading}
                              className="form-control-lg"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-end mt-4">
                        <Button
                          variant="primary"
                          onClick={handleCancel}
                          disabled={loading}
                          className="px-4 py-2 fw-semibold me-2"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="success"
                          disabled={loading || vehicles.length === 0 || drivers.length === 0}
                          className="px-4 py-2 fw-semibold"
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-check me-2" />
                              Save Service
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  );
};

export default AddServicePage;